import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";
import { OrderCourierLabel } from "src/content/applications/Orders/OrderShippingLabel";
import { pdf } from '@react-pdf/renderer';
import { fetchServiceTicketList } from "./ServiceTicketListRedux";

// Builds an order-shaped object from a service ticket so the existing OrderCourierLabel
// PDF (used for orders) renders identically for tickets.
function buildOrderDataFromTicket(ticket: any) {
    let cartProductList: any[] = [];
    try {
        const parsed = JSON.parse(ticket.descriptionList ?? "[]");
        cartProductList = parsed
            .filter((entry: any) => !entry?.timestamp)
            .map((entry: any) => ({
                amount: 1,
                sizeType: "custom",
                productDetail: {
                    productId: entry?.productId ?? "",
                    name: entry?.productName ?? entry?.category ?? "Item",
                    displayImage: entry?.productImage ?? "",
                },
            }));
    } catch (e) {
        cartProductList = [];
    }

    return {
        orderName: ticket.name ?? "Customer",
        orderId: ticket.timestamp,
        orderIdFull: ticket.timestamp + "-" + (ticket.userSub ?? ""),
        creationDateTime: ticket.timestamp,
        userSub: ticket.userSub ?? "",
        addressName: ticket.addressName ?? "",
        address: ticket.address ?? "",
        kotaKecamatanText: ticket.kotaKecamatanText ?? "",
        kodePos: ticket.kodePos ?? "",
        keterangan: ticket.keterangan ?? "",
        phoneNumber: ticket.phoneNumber ?? "",
        courierAwb: ticket.courierAwb,
        courierOrderId: ticket.courierOrderId,
        courierSelectionObject: ticket.courierSelectionObject,
        cartProductList: cartProductList,
    };
}

// Generates and downloads the courier shipping label for a ticket, using the exact same
// PDF component as orders. Mirrors OrderListRedux.fetchUserCourierLabelPdf.
export const fetchServiceTicketCourierLabelPdf = createAsyncThunk(
    'serviceTicketCourier/fetchCourierLabelPdf',
    async (arg: any) => {
        const inputData: any = {
            orderData: buildOrderDataFromTicket(arg),
        };

        const blob = pdf(<OrderCourierLabel inputData={inputData} />).toBlob();

        blob.then(function (blob) {
            const fileURL = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = "Label Courier for Service Ticket " + arg.timestamp + ".pdf";
            link.click();
        });
    }
);

// This slice mirrors the courier-pickup parts of OrderPostRedux, adapted for service
// tickets. The draft holds the ticket plus the shipping fields collected in the dialog
// (address, kecamatan, courier selection, weight, pickup schedule).

const initialState: any = {
    ticketDraft: {},

    courierDialogOpen: false,

    courierPickupErrorMessage: null,

    courierOptions: undefined,
    courierOptionsFetchStatus: APIRequestStatus.Idle,

    // Saved addresses for the ticket owner (looked up by userSub on the backend).
    addressList: undefined,
    addressListFetchStatus: APIRequestStatus.Idle,

    status: APIRequestStatus.Idle,
    error: null,
};

const SERVICE_TICKET_URL =
    'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/service-ticket';

// Fetches the saved addresses belonging to the ticket's owner. The backend resolves the
// userSub from the ticket itself (serviceTicketUpdatePost.js checkAddressList branch).
export const fetchServiceTicketAddressList = createAsyncThunk(
    'serviceTicketCourier/fetchAddressList',
    async (arg: any) => {
        const response = await axios({
            method: 'put',
            url: SERVICE_TICKET_URL,
            data: {
                serviceTicketObject: {
                    statusFinished: arg.statusFinished,
                    timestamp: arg.timestamp,
                },
                checkAddressList: true,
            },
        });

        return response.data?.addressList ?? [];
    }
);

// Fetch courier options (rates) from KiriminAja for a destination kecamatan + weight.
// Ported from web CartRedux.getCourierOptions.
export const getServiceTicketCourierOptions = createAsyncThunk(
    'serviceTicketCourier/getCourierOptions',
    async (arg: { dest: number, weight: number }) => {
        const response = await axios({
            method: 'post',
            url: "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/kirimin-aja-proxy",
            data: JSON.stringify({
                "axiosConfig": {
                    "method": "post",
                    "url": "https://client.kiriminaja.com/api/mitra/shipping_price",
                    "data": {
                        "origin": 6315,
                        "destination": arg.dest,
                        "weight": arg.weight
                    }
                }
            })
        });

        return response.data;
    }
);

// Request a pickup to KiriminAja for a service ticket, then persist the courier fields
// on the ticket. Ported from OrderPostRedux.postOrderCourierPickupRequest.
export const postServiceTicketCourierPickupRequest = createAsyncThunk(
    'serviceTicketCourier/postPickupRequest',
    async (arg, thunkAPI) => {
        const state: any = thunkAPI.getState();
        const draft = state.serviceTicketCourier.ticketDraft;

        // Generate a courier order id the same way orders do (orderPost.js).
        const courierOrderId = draft.courierOrderId ? draft.courierOrderId : "SDS-" + Date.now();

        let digits = draft.phoneNumber ?? "";
        digits = digits.slice(digits[0] === '0' ? 1 : 0)
        if (!digits.startsWith('+62')) {
          digits = '+62' + digits;
        }
    
        let phoneNum = digits.substr(3);
        if (phoneNum.length < 6) {
            phoneNum = phoneNum.padEnd(6, "0");
        }

        const destinationAddress = (
            (draft.address ?? "") + ", " + (draft.kotaKecamatanText ?? "")
            + " " + (draft.kodePos ?? "")
            + " " + (draft.keterangan ? ("(" + draft.keterangan + ")") : "")
        ).substring(0, 200);

        const axiosData = {
            method: 'post',
            url: "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/kirimin-aja-proxy",
            data: JSON.stringify({
                "axiosConfig": {
                    "method": "post",
                    "url": "https://client.kiriminaja.com/api/mitra/v6.2/request_pickup",
                    "data": {
                        "name": "Studio S.",
                        "address": "Jl. Alamanda X Blok I 10 No. 16, Jelupang, Serpong Utara, Tangerang Selatan, Banten 15323",
                        "phone": "087813028340",
                        "kecamatan_id": 6315,
                        "latitude": -6.258,
                        "longitude": 106.663,
                        "zipcode": "15323",
                        "schedule": draft.courierRequestPickupSchedule,
                        "packages": [
                            {
                                "order_id": courierOrderId,
                                "destination_name": (draft.name ?? "Customer").substring(0, 50),
                                "destination_address": destinationAddress,
                                "destination_kecamatan_id": draft.destinationKiriminAjaObject?.kecamatan_id,
                                "destination_phone": "0" + phoneNum,
                                "weight": draft.courierWeight,
                                "width": 35,
                                "height": 1 * (draft.courierWeight / 250),
                                "length": 30,
                                "item_value": 300000, // ASSUME FOR ALL PACKAGES
                                "shipping_cost": Number(draft.courierSelectionObject.cost),
                                "service": draft.courierSelectionObject.service,
                                "service_type": draft.courierSelectionObject.service_type,
                                "item_name": "Baju",
                                "package_type_id": 1,
                                "cod": 0
                            }
                        ]
                    }
                }
            })
        };

        // Request Pickup to KiriminAja
        const response1 = await axios(axiosData);

        // FAIL RESPONSE - keep dialog open, do not persist. Surface the error text.
        if (response1.data?.returnBody?.status === false
            && !response1.data?.returnBody?.packages?.[0]?.order_id) {
            return {
                success: false,
                errorMessage: response1.data?.returnBody?.text || "Request pickup gagal. Silakan coba lagi.",
            };
        }

        // SUCCESS or REPEAT RESPONSE - persist courier fields onto the ticket.
        const awb = response1.data?.returnBody?.details?.[0]?.awb ?? draft.courierAwb ?? "";

        const courierUpdate: any = {
            courierOrderId: courierOrderId,
            courierAwb: awb,
            courierSelectionObject: draft.courierSelectionObject,
            courierRequestPickupSchedule: draft.courierRequestPickupSchedule,
            courierRequestPickupScheduleObject: draft.courierRequestPickupScheduleObject ?? {},
            courierWeight: draft.courierWeight,
            addressName: draft.addressName ?? "",
            address: draft.address ?? "",
            kotaKecamatanText: draft.kotaKecamatanText ?? "",
            kodePos: draft.kodePos ?? 0,
            keterangan: draft.keterangan ?? "",
            destinationKiriminAjaObject: draft.destinationKiriminAjaObject ?? {},
        };

        const response = await axios({
            method: 'put',
            url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/service-ticket',
            data: {
                serviceTicketObject: {
                    statusFinished: draft.statusFinished,
                    timestamp: draft.timestamp,
                },
                courierUpdate: courierUpdate,
            }
        });

        thunkAPI.dispatch(fetchServiceTicketList(''));

        return { success: true, data: response.data };
    }
);

const slice = createSlice({
    name: 'serviceTicketCourier',
    initialState,
    reducers: {
        updateVal(state, action) {
            state.ticketDraft = action.payload;
        },
        addVal(state, action) {
            for (let key in action.payload) {
                state.ticketDraft[key] = action.payload[key];
            }
        },
        updateDialogOpenCourier(state, action) {
            state.courierDialogOpen = action.payload;
            // Clear any stale pickup error whenever the dialog is toggled
            state.courierPickupErrorMessage = null;
        },
        clearCourierPickupError(state) {
            state.courierPickupErrorMessage = null;
        },
        resetCourierOptions(state) {
            state.courierOptions = undefined;
            state.courierOptionsFetchStatus = APIRequestStatus.Idle;
        },
        resetAddressList(state) {
            state.addressList = undefined;
            state.addressListFetchStatus = APIRequestStatus.Idle;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchServiceTicketAddressList.pending, (state) => {
                state.addressListFetchStatus = APIRequestStatus.RequestInProgress;
                state.addressList = undefined;
            })
            .addCase(fetchServiceTicketAddressList.fulfilled, (state, action) => {
                state.addressListFetchStatus = APIRequestStatus.Success;
                state.addressList = action.payload ?? [];
            })
            .addCase(fetchServiceTicketAddressList.rejected, (state) => {
                state.addressListFetchStatus = APIRequestStatus.Failure;
                state.addressList = [];
            })
            .addCase(getServiceTicketCourierOptions.pending, (state) => {
                state.courierOptionsFetchStatus = APIRequestStatus.RequestInProgress;
                state.courierOptions = undefined;
            })
            .addCase(getServiceTicketCourierOptions.fulfilled, (state, action) => {
                state.courierOptionsFetchStatus = APIRequestStatus.Success;
                state.courierOptions = action.payload?.returnBody?.results ?? [];
            })
            .addCase(getServiceTicketCourierOptions.rejected, (state) => {
                state.courierOptionsFetchStatus = APIRequestStatus.Failure;
            })
            .addCase(postServiceTicketCourierPickupRequest.pending, (state) => {
                state.status = APIRequestStatus.RequestInProgress;
                // Clear any previous error when a new request starts
                state.courierPickupErrorMessage = null;
            })
            .addCase(postServiceTicketCourierPickupRequest.fulfilled, (state, action) => {
                if (action.payload.success) {
                    state.ticketDraft = {};
                    state.courierDialogOpen = false;
                    state.courierOptions = undefined;
                    state.courierOptionsFetchStatus = APIRequestStatus.Idle;
                    state.addressList = undefined;
                    state.addressListFetchStatus = APIRequestStatus.Idle;
                    state.courierPickupErrorMessage = null;
                } else {
                    // Keep the dialog open and show the error returned by KiriminAja
                    state.courierPickupErrorMessage = action.payload.errorMessage ?? "Request pickup gagal. Silakan coba lagi.";
                }
                state.status = APIRequestStatus.Success;
            })
            .addCase(postServiceTicketCourierPickupRequest.rejected, (state) => {
                state.status = APIRequestStatus.Failure;
                state.courierPickupErrorMessage = "Request pickup gagal karena kesalahan jaringan/server. Silakan coba lagi.";
            });
    }
});

export const { updateVal, addVal, updateDialogOpenCourier, clearCourierPickupError, resetCourierOptions, resetAddressList } = slice.actions;

export default slice.reducer;
